import { Component, OnInit, OnDestroy } from "@angular/core"; // Added OnInit, OnDestroy
import { Router } from "@angular/router";
import { Store } from '@ngrx/store'; // Added Store
import { SnackBarService } from "../../../services/snack-bar.service";
import { BtnsData } from "../../home/home.component";
import {
  Holidays as holidayOptions,
  Parashot as parashotOptions,
  WeeklyBooks,
} from "@shared/constants";
import labels from "./../data/custom.json";

// Added Imports for manual names logic
import { Observable, Subject, takeUntil, take } from "rxjs";
import { UserType } from "@shared/schemas/userSchema";
import { selectAllUsers, selectCurrentUserName } from '../../../store/slices/usersSlice/usersSlice.selector';
import { AppStore } from "../../../store/store";
import { UsersService } from "../../prayers/services/users.service";
import { addUserToStore } from "../../../store/slices/usersSlice/usersSlice.actions";

type DialogMode = "history" | "normal";

@Component({
  selector: "app-custom-prints",
  templateUrl: "./custom-prints.component.html",
  styleUrl: "./custom-prints.component.scss",
})
export class CustomPrintsComponent implements OnInit, OnDestroy {
  // --- ADDED: Store Selectors ---
  users$: Observable<UserType[]> = this.store.select(selectAllUsers);
  currentUserName$: Observable<string> = this.store.select(selectCurrentUserName);
  private destroy$: Subject<void> = new Subject<void>();

  actions: BtnsData[] = [];
  header: string = labels.header;

  showDialog = false;
  dialogMode: DialogMode = "history";

  selectedYear: string = new Date().getFullYear().toString();
  selectedParasha: string | null = null;
  selectedBook: string | null = null;

  // --- ADDED: Manual Names State ---
  manualPtihatHihal: string = '';
  // Optional manual override for the haftara name shown on the print.
  // Defaults to the parasha name on the print page when left empty.
  manualHaftara: string = '';
  manualAliyot: string[] = [];
  // CRITICAL: these strings must match the server's ALIYA_PRINT_ORDER
  // verbatim (no trailing whitespace) — the override join in
  // print-aliyot.applyManualOverrides() compares with === . The previous
  // 'תרגום ' (trailing space) silently failed to match the server's
  // 'תרגום', which is why manually-entered Targum names never appeared
  // on the print.
  aliyotLabels: string[] = [
    'עליה-ראשונה', 'עליה-שנייה', 'עליה-שלישי',
    'עליה-רביעי', 'עליה-חמישי', 'עליה-שישי',
    'עליה-שביעי', 'מפטיר', 'תרגום', 'פתיחת היכל'
  ];

  // --- ADDED: Manual Hazanim override (per-position) ---
  // If a slot is left empty, the server's hazan at that position is kept.
  // If a slot is filled, it replaces the server's hazan at that position.
  manualHazanim: string[] = [];
  hazanimLabels: string[] = ['חזן ראשון', 'חזן שני', 'חזן שלישי'];

  parashaList = [
    ...parashotOptions.map((p) => p.heb),
    ...holidayOptions.map((h) => h.heb),
  ];

  yearList: string[] = [];
  bookList = WeeklyBooks.map((b) => b.name);
  searchTerm: string = "";

  constructor(
    private router: Router, 
    private snackBar: SnackBarService,
    private store: Store<AppStore>, // Added
    private userService: UsersService // Added
  ) {
    this.actions = [
      { txt: labels.smallTable, icon: "table", link: "print-balance" },
      { txt: labels.alyot, icon: "toc", link: "print-aliyot" },
    ];

    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y <= currentYear + 3; y++) {
      this.yearList.push(y.toString());
    }

    // --- ADDED: Initialize manual arrays ---
    this.manualAliyot = new Array(this.aliyotLabels.length).fill('');
    this.manualHazanim = new Array(this.hazanimLabels.length).fill('');
  }

  ngOnInit(): void {
    // Always fetch on mount. The session restore pre-populates the store
    // with just the logged-in admin, so the "if empty" guard never fires.
    this.userService
      .getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fetched) =>
          fetched.forEach((user) => this.store.dispatch(addUserToStore({ user }))),
        error: (err) => console.error('Error fetching users:', err),
      });
  }

  onButtonClick(label: BtnsData): void {
    if (label.link === "print-aliyot") {
      this.showDialog = true;
      this.dialogMode = "normal"; 
    } else {
      this.router.navigate(["prayers/custom-prints/print-balance"]);
    }
  }

  switchMode(mode: DialogMode): void {
    this.dialogMode = mode;
    this.selectedParasha = null;
    this.selectedBook = null;
    this.searchTerm = "";
    // ADDED: Reset manual names on switch
    this.manualPtihatHihal = '';
    this.manualHaftara = '';
    this.manualAliyot.fill('');
    this.manualHazanim.fill('');
  }

  get filteredParashaList(): string[] {
    if (!this.searchTerm.trim()) return this.parashaList;
    const s = this.searchTerm.trim();
    return this.parashaList.filter((p) => p.includes(s));
  }

  onSearchTermChange(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
  }

  closeDialog(): void {
    this.showDialog = false;
  }

  submitSelection(): void {
    const queryParams: any = {};

    if (this.dialogMode === "history") {
      if (!this.selectedParasha) {
        this.snackBar.openSnackBar("בחר פרשה או חג");
        return;
      }
      queryParams.parasha = this.selectedParasha;
      queryParams.year = this.selectedYear;
    } else if (this.dialogMode === "normal") {
      if (!this.selectedBook) {
        this.snackBar.openSnackBar("בחר ספר");
        return;
      }
      queryParams.book = this.selectedBook;

      // --- ADDED: Manual names serialization ---
      const customOrders = this.aliyotLabels.map((label, index) => ({
        name: label,
        userName: this.manualAliyot[index] || ''
      }));
      queryParams.ptihatHihal = this.manualPtihatHihal;
      queryParams.orders = JSON.stringify(customOrders);

      // --- ADDED: Manual hazanim serialization (only send if any slot
      // was filled — keeps the URL clean when admin uses server defaults).
      const filledHazanim = this.manualHazanim.map(h => (h ?? '').trim());
      if (filledHazanim.some(h => h !== '')) {
        queryParams.hazanim = JSON.stringify(filledHazanim);
      }

      // --- ADDED: Manual haftara name (only send if filled).
      const trimmedHaftara = (this.manualHaftara ?? '').trim();
      if (trimmedHaftara !== '') {
        queryParams.haftara = trimmedHaftara;
      }
    }

    this.router.navigate(["prayers/custom-prints/print-aliyot"], { queryParams });
    this.closeDialog();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}